import { useState, useEffect } from "react";
import { SiteNavigation } from "../components/navigation/SiteNavigation";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { 
  Music, 
  Calendar, 
  ShoppingBag, 
  Sparkles,
  Users,
  Play,
  MapPin,
  BadgeCheck,
  Rss,
  Heart
} from "lucide-react";
import { Button } from "../components/ui/button";

const FeedPage = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchFeed();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  const fetchFeed = async () => {
    try {
      const res = await axios.get(`${API}/feed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedItems(res.data.items || []);
      setMessage(res.data.message || "");
    } catch (e) {
      console.error("Failed to fetch feed:", e);
    } finally {
      setLoading(false);
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Rss className="w-16 h-16 text-[#EC008C] mx-auto mb-4" />
          <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-3">Your Feed</h1>
          <p className="text-[#8A92A0] mb-6">Sign in to see updates from artists you follow.</p>
          <Button
            onClick={() => {
              const redirectUrl = window.location.origin + "/feed";
              window.location.href = "/auth?next=" + encodeURIComponent(window.location.pathname + window.location.search);
            }}
            className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full px-8"
            data-testid="feed-signin-btn"
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="feed-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong public-navigation-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          
          <SiteNavigation scope="feed" extraLinks={[{ path: "/feed", label: "Feed" }]} />

          <Button 
            onClick={() => navigate("/dashboard")}
            className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] font-semibold rounded-full px-6"
          >
            Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">Your Feed</h1>
            <p className="text-[#8A92A0]">Latest from artists you follow</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner"></div></div>
          ) : message ? (
            <div className="card p-12 text-center">
              <Heart className="w-16 h-16 text-[#8A92A0] mx-auto mb-4" />
              <h2 className="font-['Unbounded'] text-xl text-white mb-2">No Updates Yet</h2>
              <p className="text-[#8A92A0] mb-6">{message}</p>
              <Button onClick={() => navigate("/fandom")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full">
                Discover Artists
              </Button>
            </div>
          ) : feedItems.length === 0 ? (
            <div className="card p-12 text-center">
              <Rss className="w-16 h-16 text-[#8A92A0] mx-auto mb-4" />
              <h2 className="font-['Unbounded'] text-xl text-white mb-2">All Caught Up!</h2>
              <p className="text-[#8A92A0]">No new updates from your followed artists.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {feedItems.map((item) => (
                <FeedCard key={`${item.type}-${item.id}`} item={item} timeAgo={timeAgo} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const FeedCard = ({ item, timeAgo }) => {
  const artist = item.artist || {};
  
  const ArtistHeader = () => (
    <Link to={`/artist/${artist.artist_id}`} className="flex items-center gap-3 mb-3" data-testid={`feed-artist-${artist.artist_id}`}>
      <div className="w-10 h-10 rounded-full bg-[#1A2030] overflow-hidden flex items-center justify-center">
        {artist.profile_image ? (
          <img src={artist.profile_image} alt={artist.stage_name} className="w-full h-full object-cover" />
        ) : (
          <Users className="w-5 h-5 text-[#8A92A0]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-white font-semibold text-sm truncate">{artist.stage_name || "Unknown"}</span>
          {artist.is_verified && <BadgeCheck className="w-4 h-4 text-[#EC008C] flex-shrink-0" />}
        </div>
        <span className="text-xs text-[#6A7280]">{timeAgo(item.created_at)}</span>
      </div>
    </Link>
  );

  if (item.type === "content") {
    return (
      <div className="card p-5" data-testid={`feed-content-${item.id}`}>
        <ArtistHeader />
        <Link to={`/artist/${artist.artist_id}`} className="block group">
          <div className="flex items-center gap-4 p-3 rounded-lg bg-[#0A0E18] hover:bg-[#1a1a2e] transition-colors">
            <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#EC008C]/20 to-[#FF6BC5]/20 flex items-center justify-center flex-shrink-0">
              {item.content_type === "music" ? (
                <Music className="w-6 h-6 text-[#EC008C]" />
              ) : (
                <Play className="w-6 h-6 text-[#FF6BC5]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate group-hover:text-[#EC008C] transition-colors">{item.title}</p>
              <div className="flex items-center gap-3 text-xs text-[#6A7280]">
                <span className="capitalize">{item.content_type}</span>
                <span>{(item.plays || 0).toLocaleString()} plays</span>
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-[#EC008C]/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Play className="w-5 h-5 text-[#EC008C]" />
            </div>
          </div>
        </Link>
        {item.description && <p className="text-sm text-[#8A92A0] mt-2">{item.description}</p>}
      </div>
    );
  }

  if (item.type === "event") {
    return (
      <div className="card p-5" data-testid={`feed-event-${item.id}`}>
        <ArtistHeader />
        <Link to={`/events/${item.id}`} className="block group">
          <div className="p-4 rounded-lg bg-gradient-to-r from-[#FF6BC5]/5 to-[#EC008C]/5 border border-[#1A2030] hover:border-[#FF6BC5]/30 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-lg bg-[#FF6BC5]/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-[#FF6BC5]" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold group-hover:text-[#FF6BC5] transition-colors">{item.title}</p>
                <p className="text-sm text-[#8A92A0] flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" /> {item.venue}{item.location ? `, ${item.location}` : ""}
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className="text-[#EC008C] font-semibold">${item.ticket_price}</span>
                  <span className="text-[#6A7280]">{item.event_date}</span>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }

  if (item.type === "product") {
    return (
      <div className="card p-5" data-testid={`feed-product-${item.id}`}>
        <ArtistHeader />
        <Link to={`/products/${item.id}`} className="block group">
          <div className="flex items-center gap-4 p-3 rounded-lg bg-[#0A0E18] hover:bg-[#1a1a2e] transition-colors">
            <div className="w-14 h-14 rounded-lg bg-[#D14A85]/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {item.images && item.images.length > 0 ? (
                <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover rounded-lg" />
              ) : (
                <ShoppingBag className="w-6 h-6 text-[#D14A85]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate group-hover:text-[#D14A85] transition-colors">{item.title}</p>
              <span className="text-xs text-[#6A7280] capitalize">{item.category}</span>
            </div>
            <span className="text-[#EC008C] font-bold">${item.price}</span>
          </div>
        </Link>
      </div>
    );
  }

  return null;
};

export default FeedPage;
